import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/utils/cn";
import { Loader } from "@/components/ui/Loader";
import { onlyDigits } from "@/utils/brDocument";

function matchesSearchableLabel(label: string, search: string): boolean {
  const q = search.trim();
  if (!q) return true;
  const qLower = q.toLowerCase();
  if (label.toLowerCase().includes(qLower)) return true;

  const qDigits = onlyDigits(q);
  if (qDigits.length >= 3) {
    const labelDigits = onlyDigits(label);
    if (labelDigits.includes(qDigits)) return true;
  }
  return false;
}

export interface SearchableSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  labelBold?: boolean;
}

export interface SearchableSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  dropdownMinWidth?: number;
  listMaxHeightClass?: string;
  stopPropagation?: boolean;
}

export function SearchableSelect({
  value = "",
  onChange,
  options,
  placeholder = "Selecione...",
  label,
  error,
  hint,
  loading = false,
  disabled = false,
  className,
  id,
  dropdownMinWidth,
  listMaxHeightClass = "max-h-[min(20rem,50vh)]",
  stopPropagation = true,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const inputId = id ?? `searchable-select-${label?.toLowerCase().replace(/\s+/g, "-") ?? "field"}`;

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const minW = dropdownMinWidth ?? 0;
    let width = Math.max(rect.width, minW);
    width = Math.min(width, vw - 2 * margin);
    let left = rect.left;
    if (left + width > vw - margin) left = vw - margin - width;
    if (left < margin) left = margin;
    setDropdownPos({
      top: rect.bottom + 4,
      left,
      width,
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, dropdownMinWidth]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, dropdownMinWidth]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 60);
  }, [open]);

  const filtered = options.filter((o) => matchesSearchableLabel(o.label, search));

  const selectedLabel = options.find((o) => o.value === value)?.label;
  const hasSelection = Boolean(value);

  const selectOption = (option: SearchableSelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    setSearch("");
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((v) => !v);
    }
  };

  const handleDropdownKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      setSearch("");
      triggerRef.current?.focus();
    }
  };

  const triggerLabel = loading
    ? "Carregando..."
    : selectedLabel ?? placeholder;

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          onKeyDown={handleDropdownKeyDown}
          onMouseDown={(e) => {
            if (stopPropagation) e.stopPropagation();
          }}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          className="bg-white rounded-xl border border-slate-200 shadow-modal overflow-hidden"
          role="radiogroup"
        >
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar..."
                className="w-full h-8 pl-8 pr-8 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-brand-600"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <ul className={cn("overflow-y-auto py-1", listMaxHeightClass)}>
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-slate-400 text-center">
                Nenhum resultado encontrado.
              </li>
            ) : (
              filtered.map((option) => {
                const selected = value === option.value;
                const isDisabled = option.disabled === true;
                return (
                  <li
                    key={option.value}
                    role="radio"
                    aria-checked={selected}
                    aria-disabled={isDisabled}
                    onClick={() => selectOption(option)}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2 text-sm select-none transition-colors",
                      isDisabled
                        ? "cursor-not-allowed text-slate-500"
                        : "cursor-pointer",
                      !isDisabled && selected
                        ? "bg-brand-50 text-brand-700"
                        : !isDisabled && "text-slate-700 hover:bg-slate-50",
                    )}
                  >
                    <div
                      className={cn(
                        "w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors bg-white",
                        isDisabled
                          ? "border-slate-200 opacity-50"
                          : selected
                            ? "border-brand-600"
                            : "border-slate-300",
                      )}
                    >
                      {selected && !isDisabled && (
                        <div className="w-2 h-2 rounded-full bg-brand-600" />
                      )}
                    </div>
                    <span
                      className={cn(
                        "min-w-0 flex-1 break-words leading-snug",
                        option.labelBold && "font-bold",
                      )}
                    >
                      {option.label}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
          {options.length > 0 && (
            <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-400 text-center">
              {search.trim()
                ? `${filtered.length} de ${options.length} itens`
                : `${options.length} itens disponíveis`}
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-semibold text-slate-500 uppercase tracking-wide"
        >
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        id={inputId}
        type="button"
        disabled={disabled || loading}
        onClick={() => {
          if (!disabled && !loading) setOpen((v) => !v);
        }}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          "w-full h-10 rounded-lg border bg-slate-50 pl-3 pr-3 text-sm text-left",
          "flex items-center gap-2 transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-offset-0",
          open
            ? "border-brand-600 ring-2 ring-brand-600"
            : error
              ? "border-red-400 focus:ring-red-400 focus:border-red-400"
              : "border-slate-200 hover:border-slate-300 focus:ring-brand-600 focus:border-brand-600",
          (disabled || loading) && "opacity-60 cursor-not-allowed",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cn("flex-1 text-left line-clamp-2 leading-snug", !hasSelection && "text-slate-400")}>
          {triggerLabel}
        </span>

        <div className="flex items-center gap-1 flex-shrink-0">
          {hasSelection && !loading && !disabled && (
            <span
              role="button"
              title="Limpar seleção"
              onClick={clearSelection}
              className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer p-0.5 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}

          {loading ? (
            <Loader size="xs" color="slate" />
          ) : (
            <ChevronDown
              className={cn(
                "w-4 h-4 text-slate-400 transition-transform duration-150",
                open && "rotate-180",
              )}
            />
          )}
        </div>
      </button>

      {error && <p className="text-xs text-red-500 font-medium">{error}</p>}
      {hint && !error && <p className="text-xs text-slate-400">{hint}</p>}

      {dropdown}
    </div>
  );
}
